/**
 * Main entry point for Vite build
 * 
 * Unified entry point: initializes DB, app UI, sync triggers, and service worker.
 * The legacy public/app.js has been migrated to src/app.js.
 */

// Import styles (Vite will bundle these)
import './styles/style.css';

// Core modules
import { initDB, migrateFromLocalStorage, getPendingCount } from './db.js';
import { appState } from './state.js';
import { api, ApiError } from './api.js';

// App UI
import { initApp } from './app.js';

// Sync
import { setupSyncTriggers } from './sync.js';

// Service worker
import { registerServiceWorker } from './sw-registration.js';
import { updateBanner } from './ui/update-banner.js';

/**
 * Application initialization
 */
async function init() {
  console.log('[Main] Initializing...');

  try {
    // Initialize IndexedDB
    const db = await initDB();
    console.log('[Main] IndexedDB initialized');

    // Migrate legacy localStorage data if present
    const migrated = await migrateFromLocalStorage(db);
    if (migrated > 0) {
      console.log(`[Main] Migrated ${migrated} items from localStorage`);
    }

    // Update pending count in state
    const pendingCount = await getPendingCount(db);
    appState.set('pendingCount', pendingCount);

    // Initialize the app UI (form handling, auth, config)
    await initApp(db);
    console.log('[Main] App UI initialized');

    // Setup sync triggers (online, visibility, focus)
    setupSyncTriggers(db);

    // Register service worker with update handling
    registerServiceWorker({
      onUpdateAvailable: (newWorker) => {
        console.log('[Main] Update available');
        updateBanner.show(newWorker, {
          onUpdate: () => {
            console.log('[Main] User triggered update');
          },
          onDismiss: () => {
            console.log('[Main] Update deferred');
          }
        });
      }
    });

    console.log('[Main] Ready');
  } catch (error) {
    console.error('[Main] Initialization failed:', error);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Export for module consumers
export { appState, api, ApiError };
